import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as yup from "yup";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import LoadingButton from "@/components/common/buttons/loadingButton";
import PageContainer from "@/components/common/pageContainer/pageContainer";
import PageHeader from "@/components/common/pageHeader/pageHeader";
import { GREY } from "@/config/mui/colorPalette";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { useReturningClientEditPersonalDataMutation } from "@/graphql/mutations/returningClientEditPersonalData.graphql.types";
import useAction from "@/hooks/misc/useAction";
import { BookingStep, ClientDetailsInput } from "@/types/bookingTypes";
import { BookingReturningClientErrors } from "@/utils/booking";
import { hasDjangoMutationError } from "@/utils/djangoMutationError";
import useErrorLogger from "@/utils/useErrorLogger";
import BookingInfoCard from "../bookingInfoCard";

const schema = yup.object().shape({
  firstName: yup.string().required("Enter your first name."),
  lastName: yup.string().required("Enter your last name."),
});

export default function BookingReturningClientEditDetails() {
  const {
    setStep,
    setClientDetails,
    clientDetails,
    code,
    medspaData,
    setCodeExpired,
  } = useClientBookingContext();

  const logError = useErrorLogger();

  const [changeClientDetails] = useReturningClientEditPersonalDataMutation();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ClientDetailsInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: clientDetails.firstName,
      lastName: clientDetails.lastName,
    },
    mode: "onTouched",
  });

  const onSubmit = async ({ firstName, lastName }: ClientDetailsInput) => {
    try {
      await changeClientDetails({
        variables: {
          firstName,
          lastName,
          code,
          medspaSlug: medspaData.slug,
          clientPhoneNumber: clientDetails.phone,
        },
      });
      setClientDetails((prev) => ({ ...prev, firstName, lastName }));
      setStep(BookingStep.RETURNING_CLIENT);
    } catch (e) {
      if (e.message === BookingReturningClientErrors.CODE_EXPIRED) {
        setCodeExpired(true);
        setStep(BookingStep.CODE_INPUT);
        return;
      }
      logError(e.message);
      toast.error(
        hasDjangoMutationError(e)
          ? e.message
          : "Unable to save client details. Please contact support team."
      );
    }
  };

  const { handleAction, isLoading } = useAction(onSubmit);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: GREY[5],
        zIndex: 9999,
      }}
    >
      <PageContainer>
        <PageHeader
          title="Edit personal data"
          goBackStrategy="onClick"
          goBackOnClick={() => setStep(BookingStep.RETURNING_CLIENT)}
        />
        <AdditionalInfo
          variant="info"
          text={`If you want to change your email address or phone number, please contact ${medspaData.name}.`}
          sx={{ mb: 3 }}
        />
        <BookingInfoCard>
          <Typography
            variant="h3"
            sx={{
              color: "text.primary",
              pb: 3,
            }}
          >
            Enter your details below
          </Typography>
          <form style={{ width: "100%" }}>
            <Stack spacing={3}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="First name"
                    {...field}
                    error={!!errors.firstName}
                    helperText={
                      errors.firstName
                        ? (errors.firstName.message as string)
                        : ""
                    }
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="Last name"
                    {...field}
                    error={!!errors.lastName}
                    helperText={
                      errors.lastName ? (errors.lastName.message as string) : ""
                    }
                  />
                )}
              />
            </Stack>
          </form>
          <LoadingButton
            fullWidth
            sx={{ mt: 3 }}
            variant="contained"
            onClick={handleSubmit(handleAction)}
            loading={isLoading}
          >
            Save changes
          </LoadingButton>
        </BookingInfoCard>
      </PageContainer>
    </Box>
  );
}
